import {useLegacyGrpcRequest} from '@aboutyou/data-fetcher';
import {RecurringPaymentMethod} from '@aboutyou/grpc-clients/grips/protos/payments/v1/payment_info.types';
import {PaymentsService_getRecurringPaymentMethods} from '@aboutyou/grpc-clients/grips/protos/payments/v1/payments_service.grpc';
import {useLocale} from '@aboutyou/i18n';
import {Log} from '@aboutyou/mlm';
import {useSessionStorage} from '@aboutyou/storage';
import {useCustomerId, useUser, useUserToken} from '@aboutyou/user';
import {verifyAsCashOnDeliveryCountry} from '../utils/bank';

enum BANK_DATA_SECTION {
  UNKNOWN = '0',
  VISIBLE = '1',
  HIDDEN = '2',
}

interface BankData {
  firstName: string;
  iban: string;
  lastName: string;
}
interface CODPaymentInformation {
  id: string;
  bankData: BankData;
}

export const useShowBandData = (
  enableRecurringPaymentMethodsRequest: boolean,
) => {
  const userId = useCustomerId();
  const key = `showBankData-${userId}`;
  const locale = useLocale();
  const user = useUser();
  const userToken = useUserToken();
  const [bankDataSection, setBankDataSection] = useSessionStorage({
    key,
    defaultValue: BANK_DATA_SECTION.UNKNOWN,
  });

  const disabled =
    !enableRecurringPaymentMethodsRequest ||
    bankDataSection !== BANK_DATA_SECTION.UNKNOWN ||
    !user.data?.checkoutSession ||
    !verifyAsCashOnDeliveryCountry(locale);

  const response = useLegacyGrpcRequest(
    PaymentsService_getRecurringPaymentMethods,
    {
      checkoutSession: user.data!.checkoutSession!,
      session: {
        authToken: userToken!,
      },
    },
    {
      disabled,
      onError: (error: any) => {
        Log.error(`@aboutyou/logic-components`, error.message, {
          level: error.service === 'update' ? 'fatal' : 'error',
          statusCode: error.statusCode,
          error: new Error(error.message),
          additionalData: {
            service: `GRPC ${error.service} recurring payment failed`,
          },
        });
      },
    },
  );

  const paymentMethod = mapToPaymentInformation(
    response.data?.paymentInformation.find(codPaymentMethod),
  );

  if (!disabled && !response.isLoading) {
    if (paymentMethod?.id) {
      setBankDataSection(BANK_DATA_SECTION.VISIBLE);
    } else {
      setBankDataSection(BANK_DATA_SECTION.HIDDEN);
    }
  }

  return Boolean(String(bankDataSection) === BANK_DATA_SECTION.VISIBLE);
};

const initialBankData = {
  firstName: '',
  lastName: '',
  iban: '',
};

function mapToPaymentInformation(
  paymentMethod: RecurringPaymentMethod | undefined,
): CODPaymentInformation | undefined {
  if (!paymentMethod) {
    return undefined;
  }

  return {
    id: paymentMethod.id,
    bankData: {
      ...(paymentMethod.type?.bankPaymentMethod.bankAccount || initialBankData),
    },
  };
}

const codPaymentMethod = (paymentMethod: RecurringPaymentMethod) =>
  paymentMethod.type?.$case === 'bankPaymentMethod';
